import { Component, Input } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { first } from 'rxjs/operators';
import { DocumentosModalComponent } from '../../../Envios/documentos/components/documentos-modal/documentos-modal.component';
import { GenericoTipoDocumento, TipoDocumento } from '../../../Envios/documentos/models/tipo-documento';
import { SpinnerModalSqpComponent } from 'src/app/shared/components/sqp-components/sqpModales/spinner-modal-sqp/spinner-modal-sqp.component';
import { ModalMessageComponent } from 'src/app/shared/components/modals/modal-message/modal-message.component';
import { EnvioSimpleFlowService } from '../../services/envio-simple-flow.service';
import {
  CatalogoGenericoItem,
  EnvioSimpleDocumentoApiService
} from '../../services/envio-simple-documento-api.service';

@Component({
  selector: 'app-envio-simple-zona-carga',
  templateUrl: './envio-simple-zona-carga.component.html',
  styleUrls: ['./envio-simple-zona-carga.component.css']
})
export class EnvioSimpleZonaCargaComponent {
  /** Muestra el separador y el boton de seleccion de documento generico. */
  @Input() mostrarGenerico = true;

  readonly textoDropzone = 'Arrastra aqui el documento pdf\no haz click para seleccionarlo';

  constructor(
    private envioSimpleFlowService: EnvioSimpleFlowService,
    private envioSimpleDocumentoApiService: EnvioSimpleDocumentoApiService,
    private dialog: MatDialog
  ) {}

  onFilesSelected(files: FileList): void {
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (!file) continue;
      this.envioSimpleFlowService.agregarDocumento({
        nombre: file.name,
        origen: 'subido',
        archivo: file
      });
    }
  }

  async abrirModalDocumentoGenerico(): Promise<void> {
    const spinnerRef: MatDialogRef<SpinnerModalSqpComponent> = this.dialog.open(SpinnerModalSqpComponent);
    let documentos: TipoDocumento[] = [];

    try {
      documentos = await this.cargarDocumentosGenericos();
    } finally {
      spinnerRef.close();
    }

    const dialogRef = this.dialog.open(DocumentosModalComponent, {
      data: {
        documentos,
        objetoEnvio: {
          tiposDocumento: this.obtenerGenericosYaAnadidos()
        },
        origenModal: 'envio-simple-generico',
        filtroGenerico: true
      },
      panelClass: 'modal-class-detalle',
      position: { right: '0' },
      autoFocus: 'dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      const seleccion: TipoDocumento[] = result?.data;
      if (!seleccion?.length) return;
      this.agregarGenericosSeleccionados(seleccion);
    });
  }

  private obtenerGenericosYaAnadidos(): TipoDocumento[] {
    return this.envioSimpleFlowService.documentos
      .filter(doc => doc.origen === 'generico' && doc.tipoDocumento)
      .map(doc => doc.tipoDocumento as TipoDocumento);
  }

  private async cargarDocumentosGenericos(): Promise<TipoDocumento[]> {
    try {
      const catalogo = await this.envioSimpleDocumentoApiService
        .getCatalogoGenericos()
        .pipe(first())
        .toPromise();
      return (catalogo ?? []).map(item => this.mapCatalogoATipoDocumento(item));
    } catch {
      this.dialog.open(ModalMessageComponent, {
        data: {
          mensaje: 'No se pudo cargar el catalogo de documentos genericos. Intentalo de nuevo.',
          alert: true
        }
      });
      return [];
    }
  }

  private mapCatalogoATipoDocumento(item: CatalogoGenericoItem): TipoDocumento {
    return {
      idTipoDocumento: item.id,
      nombre: item.nombre,
      generico: true,
      tipoDoc: GenericoTipoDocumento.plantilla,
      esCopiaBasica: false,
      formatoNombreArchivoFirmado: '',
      formatoNombreArchivoAuditTrail: '',
      split: '',
      paginas: 0,
      idPdf: '',
      esRLT: false
    } as TipoDocumento;
  }

  private async agregarGenericosSeleccionados(seleccion: TipoDocumento[]): Promise<void> {
    const spinnerRef: MatDialogRef<SpinnerModalSqpComponent> = this.dialog.open(SpinnerModalSqpComponent);
    const fallidos: string[] = [];

    try {
      for (const tipoDoc of seleccion) {
        try {
          await this.agregarGenericoIndividual(tipoDoc);
        } catch {
          fallidos.push(tipoDoc.nombre);
        }
      }
    } finally {
      spinnerRef.close();
    }

    if (fallidos.length) {
      this.avisarErrorGenericos(fallidos);
    }
  }

  private async agregarGenericoIndividual(tipoDoc: TipoDocumento): Promise<void> {
    const respuesta = await this.envioSimpleDocumentoApiService
      .getDocumentoGenerico(tipoDoc.idTipoDocumento)
      .toPromise();

    if (!respuesta?.urlPdf) {
      throw new Error('Respuesta de documento generico invalida');
    }

    const pdfBlob = await this.descargarPdf(respuesta.urlPdf);

    this.envioSimpleFlowService.agregarDocumento({
      nombre: respuesta.configuracion?.nombre ?? tipoDoc.nombre,
      origen: 'generico',
      generico: true,
      idTipoDocumento: respuesta.configuracion?.idTipoDocumento ?? tipoDoc.idTipoDocumento,
      tipoDocumento: {
        ...tipoDoc,
        ...respuesta.configuracion,
        nombre: respuesta.configuracion?.nombre ?? tipoDoc.nombre
      },
      pdfBlob,
      configuracion: respuesta.configuracion
    });
  }

  private async descargarPdf(urlPdf: string): Promise<Blob> {
    const respuesta = await fetch(urlPdf);
    if (!respuesta.ok) {
      throw new Error('No se pudo descargar el PDF del documento generico');
    }
    const blob = await respuesta.blob();
    return blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' });
  }

  private avisarErrorGenericos(fallidos: string[]): void {
    const mensaje = fallidos.length === 1
      ? `No se pudo añadir el documento "${fallidos[0]}". Inténtalo de nuevo.`
      : `No se pudieron añadir algunos documentos: ${fallidos.join(', ')}. Inténtalo de nuevo.`;

    this.dialog.open(ModalMessageComponent, {
      data: { mensaje, alert: true }
    });
  }
}
